import Immutable from "immutable";
import { isActive } from "reducers/variables/helpers";

/**
* Returns index of response in list
* @param {Immutable.List} list - response list
* @param {String} id - response id to search for
* @returns {Number}
*/
export function getResponseIndex(list, id) {
  return list.findIndex(listItem => listItem.id === id);
}

export const getMessagePath = (responseIndex, language, messageIndex, nestedPath = []) => ([
  responseIndex,
  "messages",
  language,
  messageIndex,
  ...nestedPath
]);

/**
 * @param {Immutable.Map} state
 * @param {Array.<String>} messagePath
 * @returns {Immutable.Map}
 */
export function deleteVariableFromCapture(state, messagePath) {
  return state.setIn([...messagePath, "variableId"], null);
}

/**
 * @param {Immutable.Map} state
 * @param {Array.<String>} messagePath
 * @returns {Immutable.Map}
 */
export function deleteVariableFromListSelection(state, messagePath) {
  return state.setIn([...messagePath, "variableId"], null);
}

export const deleteVariableFromHTTP = (state, messagePath, variableId) => (
  state.updateIn(
    [...messagePath, "variablesData"],
    variablesData => variablesData.filter(data => data.get("variableId") !== variableId)
  )
);

export const deleteVariableFromVariableOverride = (state, messagePath, variableId) => (
  state.updateIn(
    [...messagePath, "variablesData"],
    variablesData => variablesData.filter(data => data.get("variableId") !== variableId)
  )
);

export const getVariableIdToAttach = (action) => {
  const {
    variable, existingVar, newVariable
  } = action;
  const existingVariable = existingVar;
  let variableToUse = existingVariable || newVariable || variable;
  const existingIsActive = existingVariable && isActive(existingVariable);

  if (newVariable === null) {
    variableToUse = null;
  }

  return {
    id: variableToUse ? variableToUse.id : null,
    existingIsActive
  };
};

export const deleteVariableFromMessage = (state, action, messagePath) => {
  const messageType = state.getIn([...messagePath, "type"]);
  const { id: variableId } = getVariableIdToAttach(action);

  if (messageType === "capture") {
    return deleteVariableFromCapture(state, messagePath);
  }

  if (messageType === "list_selection_template") {
    return deleteVariableFromListSelection(state, messagePath);
  }

  if (messageType === "http_request_recipe" || messageType === "widget") {
    return deleteVariableFromHTTP(state, messagePath, variableId);
  }

  if (messageType === "variable_override") {
    return deleteVariableFromVariableOverride(state, messagePath, variableId);
  }

  return state;
};

export const attachVariableToCapture = ({
  state,
  messagePath,
  variableId,
  variableName
}) => {
  const dataToMerge = { variableId, captureKey: variableName };

  return state.mergeIn(messagePath, dataToMerge);
};

export const attachVariableToHTTP = (state, messagePath, features, {
  variableId, variableListIndex
}) => (
  state.updateIn(
    [...messagePath, "variablesData"],
    (variablesData) => {
      let updatedVariablesData = variablesData;
      const dataToMerge = {
        variableId
      };

      // because 0 is falsy, need the second part of the condition
      if (variableListIndex || variableListIndex === 0) {
        updatedVariablesData = variablesData.update(
          variableListIndex,
          data => data.merge(dataToMerge)
        );
      } else {
        updatedVariablesData = variablesData.push(Immutable.Map(dataToMerge));
      }

      return updatedVariablesData;
    }
  )
);

const attachVariableToVariableListSelection = (state, messagePath, {
  variableId
}) => {
  const dataToMerge = { variableId };

  return state.mergeIn(messagePath, dataToMerge);
};

export const attachVariableToVariableOverride = (state, messagePath, {
  variableId, variableListIndex
}) => (
  state.updateIn(
    [...messagePath, "variablesData"],
    (variablesData) => {
      let updatedVariablesData = variablesData;
      const dataToMerge = {
        variableId
      };

      // because 0 is falsy, need the second part of the condition
      if (variableListIndex || variableListIndex === 0) {
        updatedVariablesData = variablesData.update(
          variableListIndex,
          data => data.merge(dataToMerge)
        );
      } else {
        updatedVariablesData = variablesData.push(Immutable.Map(dataToMerge));
      }

      return updatedVariablesData;
    }
  )
);

export const attachVariableToMessage = (state, action, messagePath) => {
  const { variableListIndex } = action;
  const messageType = state.getIn([...messagePath, "type"]);
  const { id: variableId, existingIsActive } = getVariableIdToAttach(action);
  const variable = action.existingVar || action.updatedVar || action.newVariable || action.variable;
  const variableName = variable && variable.name;

  if (messageType === "capture") {
    return attachVariableToCapture({
      state,
      messagePath,
      variableId,
      variableName,
      features: action.featureFlags
    });
  }

  if (messageType === "http_request_recipe" || messageType === "widget") {
    return attachVariableToHTTP(
      state,
      messagePath,
      action.featureFlags,
      {
        variableId, existingIsActive, variableListIndex
      }
    );
  }

  if (messageType === "variable_override") {
    return attachVariableToVariableOverride(
      state,
      messagePath,
      {
        variableId, variableListIndex
      }
    );
  }

  if (messageType === "list_selection_template") {
    return attachVariableToVariableListSelection(
      state,
      messagePath,
      {
        variableId
      }
    );
  }

  return state;
};

export const updateVariableIdHTTP = (state, messagePath, oldId, newId) => state.updateIn(
  [...messagePath, "variablesData"],
  variablesData => (
    variablesData.map(
      data => (data.get("variableId") === oldId ? data.set("variableId", newId) : data)
    )
  )
);

export const updateVariableIdsFromServer = (state, action) => {
  /*
    Update all ids in state to match the one the server responded with.
  */
  let newState = state;
  const { variable } = action;
  const savedVariableId = action.response.data.variable._id;
  const responseIds = variable.responseReferenceCounts.keySeq().toList();
  responseIds
    .map(responseId => getResponseIndex(state, responseId))
    .forEach((responseIndex) => {
      state.getIn([responseIndex, "messages"])
        .forEach((messages, language) => {
          messages.forEach((message, messageIndex) => {
            const messagePath = getMessagePath(
              responseIndex,
              language,
              messageIndex
            );
            const messageType = message.type;

            if (
              message.variableId === variable.id ||
              message.variablesData.map(v => v.get("variableId")).includes(variable.id)
            ) {
              if (messageType === "capture") {
                newState = attachVariableToCapture({
                  state: newState,
                  messagePath,
                  variableId: savedVariableId
                });
              } else if (messageType === "http_request_recipe" || messageType === "variable_override" || messageType === "widget") {
                newState = updateVariableIdHTTP(
                  newState, messagePath, variable.id, savedVariableId
                );
              }
            }
          });
        });
    });

  return newState;
};
